/**
 * ═══════════════════════════════════════════════════════════════════════════
 * CREW DAY RESOLUTION — the ONE answer to "which days does this week hold?"
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * 🚨 THIS MODULE EXISTS BECAUSE TWO ANSWERS TO THAT QUESTION COST REAL MONEY.
 * Everything below was module-private inside `client/src/pages/BudgetCostCodes.js`
 * and served ONE editor, the crew day grid. It was extracted on 2026-08-22 when
 * the Crew Gantt gained a SECOND editor for the same data (per-day toggles on an
 * expanded member row). **The functions were MOVED, not copied, and not
 * rewritten** — behaviour is what the day grid shipped with, including the
 * quirks documented at each one.
 *
 * 🔑 THE RULE, and it is the whole reason this file has a name of its own:
 * **a surface that edits `crew_member_days` calls THESE functions. It does not
 * write a day-resolution loop of its own.** Two implementations of one question
 * IS the defect — it is not a risk of one. The day grid's own render and its
 * save resolver disagreed for exactly one week in August 2026: a 4.2-week
 * engagement was costed as 2.2 weeks of labour, ~48% understated, silently and
 * permanently (every row `PUT /days` writes carries `is_presumed = 0`, which
 * exempts the member from re-derivation for good), and it reached live staging.
 * The full account is in the docblock on `resolveWeekDays` below — kept verbatim
 * because it is the argument, not the anecdote.
 *
 * ─── WHAT A CALLER OWES ────────────────────────────────────────────────────
 * These are PURE functions over explicit arguments. They hold no state, read no
 * component scope and fetch nothing — which is what lets two surfaces with quite
 * different state shapes share them. A caller supplies:
 *   • `overlay`   — `{ [week_start]: number[] }`, ONLY the weeks the user has
 *                   touched this session. Never a full copy of the member's days.
 *   • `baseline`  — from `buildWeekDayBaseline(member, weeks)`, seeded ONCE when
 *                   the editor opens, from the member's STORED rows.
 *   • the CURRENT engagement range — the dates as they stand right now,
 *     INCLUDING an unsaved bar drag or a typed date. Passed as `{ start, end }`
 *     to `resolveWeekDays` / `dayPayloadHasChanges`, and as `startDate` /
 *     `endDate` to the two builders, which derive it themselves so a caller
 *     cannot hand the payload one range and the picture another.
 *
 * 🔻 IT USED TO BE `fallbackAllocFor(weekStart)` AND THAT IS GONE (2026-08-23).
 * Each surface kept its own one-line allocation lookup — `weekFallbackAlloc`
 * and `ganttDayFallbackAlloc` in `pages/BudgetCostCodes.js`, both since
 * DELETED — and both carried `parseFloat(default_allocation) || 1.0`, the
 * falsy-collapse this codebase has now recorded five times. The owner's ruling
 * that new days are ALWAYS Mon–Fri made the allocation the wrong input to the
 * question, not merely a badly-read one; see `DEFAULT_WORKED_DAY_INDICES`.
 * 🔀 2026-10-19 the owner made the DEFAULT the input again — for days a date
 * change ADDS only, read through `addedDayIndicesForDefault` (never `|| 1.0`).
 *
 * ─── WHAT THIS MODULE DELIBERATELY DOES NOT DO ─────────────────────────────
 * It reads no rate column and computes no money. `server/tests/regressions/
 * crew-cost-path-parity-manifest.test.js` §4 scans `client/src` for
 * `total_weeks|grade_weekly_rate|effective_weekly_rate|weekly_rate_override`;
 * this file must never match, and if it ever does it needs a declared verdict.
 */

import { dayCountForAllocation, allocationForDayCount } from '../constants/budget';
import { daysForPreset, dateForWeekDay } from '../components/budget/CrewDayGrid';

/* ────────────────────────────────────────────────
   Helper: generate Monday-based weeks between two dates
   ──────────────────────────────────────────────── */
/**
 * 🔴 END-INCLUSIVE (`while (current <= end)`) — OWNER RULING, 2026-08-24:
 * **"When a buyer types an End Date, that person DOES work that day. It is
 * their LAST WORKING DAY (inclusive)."** This loop now matches
 * `server/helpers/crewWeeks.js#enumerateCrewWeeks` exactly. Until that date it
 * was `current < end`, so a member whose `end_date` fell on a Monday had a
 * week the server costs and neither the grid nor the Gantt ever drew — the
 * phantom-charged-week defect (`docs/plans/2026-08-24-monday-end-date-phantom-week.md`).
 * The fix moved NO money: the server already charged that week; every client
 * surface grew to show it. 🔒 The agreement is pinned CROSS-SIDE by the
 * owner's worked example (17 Aug 2026 → 24 Aug 2026 = 2 weeks) reproduced
 * verbatim in BOTH suites — `crewDayResolution.endInclusive.test.js` here,
 * `server/tests/helpers/crewWeeks.differential.test.js` §3 there — so
 * re-diverging EITHER bound goes red. Do not "tidy" this bound.
 */
export function generateWeeks(startDate, endDate) {
  if (!startDate || !endDate) return [];
  const weeks = [];
  // Find the Monday on or before startDate
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return [];
  // Roll back to Monday
  const day = start.getDay(); // 0=Sun,1=Mon,...
  const diff = day === 0 ? 6 : day - 1;
  const monday = new Date(start);
  monday.setDate(monday.getDate() - diff);

  const current = new Date(monday);
  while (current <= end) {
    const yyyy = current.getFullYear();
    const mm = String(current.getMonth() + 1).padStart(2, '0');
    const dd = String(current.getDate()).padStart(2, '0');
    weeks.push(`${yyyy}-${mm}-${dd}`);
    current.setDate(current.getDate() + 7);
  }
  return weeks;
}

/* ────────────────────────────────────────────────
   THE CREW DAY EDITORS' BASELINE — 6th/7th-day premium wage feature, Task 6.

   "What day set does this member CURRENTLY have, per week, before the user
   touches anything?" An editor edits an OVERLAY on top of this, so a week
   nobody touched has no overlay entry and cannot drift.

   🔑 TWO SOURCES, IN THIS ORDER, AND THE ORDER IS THE POINT:
    1. The member's stored `crew_member_days` rows (`member.days`, always an
       array on this endpoint — "no rows recorded" is a different claim from
       "we did not look", and the server says so deliberately).
    2. If there are NO rows at all, the week's ALLOCATION, converted with the
       same `Math.round(alloc * 5)` Monday-first rule the boot backfill uses
       (`server/helpers/crewDayBackfill.js`). An editor that seeded itself any
       other way would show a member days they are not being charged for.

   🪤 A member with no rows is NOT the same as a member with zero worked days
   — `server/helpers/crewDays.js` costs the first one on the fraction path and
   the second at nothing, and `crew_member_days` cannot yet tell them apart
   (ledger entry S2-F). This function never invents that distinction: with no
   rows it shows the allocation's implied days, which is exactly what the
   member is being charged for today.

   ⚠️ NOTE FOR THE GANTT, ADDED 2026-08-22. This no-rows fallback is
   ALLOCATION-DRIVEN, and `CrewGanttView.js#isDayWorked`'s no-rows default is
   MON–FRI FLAT. They agree at allocation 1.0 and DISAGREE below it (0.6 gives
   Mon–Wed here, Mon–Fri there). That disagreement PREDATES this extraction and
   the Mon–Fri side is an owner ruling, so it was deliberately NOT collapsed —
   see the header block at `CrewGanttView.js#isDayWorked` and §6 of
   `CrewGanttView.dayToggle.test.js`, which pins the seam rather than hiding it.
   ──────────────────────────────────────────────── */
export const EMPTY_DAY_BASELINE = {
  base: {}, presumedWeeks: new Set(), hasDayRows: false, offWeeks: new Set(), range: null,
};

/**
 * 🔀 "DEFAULT ALLOCATION" — OWNER RULING, Pete, 2026-10-19: *"hand set days
 * are for dragging only. so if 3 weeks are already drawn changing to 3 days
 * allocation does nothing to those 3 weeks but a drag now defaults to 3 days."*
 * So a day a date change brings into range is drawn iff it is one of its
 * week's first N weekdays, N = the CURRENT default (the draft's, when the
 * level-2 panel or the crew table changed it in the same edit) — plus the
 * moved first/last day (10-07). It SUPERSEDES the 08-23/10-06 "Mon–Fri for
 * everyone, the allocation is not consulted" — with NO exception for 0: Pete,
 * the same day, *"I dont think we should have an off option. change it to 0
 * days"*, so a 0-day default adds only the dragged-to day.
 * Server twin: `server/helpers/crewDays.js#addedDayCountForDefault`; parity
 * pinned by `crewDaysShownEqualsCosted` (both halves read one table).
 */

/** The day indices (index 0 is MONDAY) a date change lays in a week it brings
 *  into range, at `defaultAllocation`. Absent/unparseable reads as a full week
 *  (`dateChangeWeekOverrides`' own reading); `0` is 0 days, never a falsy test. */
export function addedDayIndicesForDefault(defaultAllocation) {
  const raw = defaultAllocation;
  const a = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
  const n = Number.isFinite(a) ? Math.min(5, dayCountForAllocation(a)) : 5;
  return DEFAULT_WORKED_DAY_INDICES.slice(0, n);
}

/**
 * The baseline with the DRAFT default governing the days a date change adds.
 * `buildWeekDayBaseline` is seeded ONCE from the STORED member, so it carries
 * the stored default; a page whose form changed the default in the same edit
 * lays this over it, so the picture is what the save lays (the server reads
 * the post-write default). `undefined` = no draft default: the baseline as is.
 * Identity kept when nothing changes.
 */
export function withDraftDefaultAllocation(baseline, draftDefault) {
  if (draftDefault === undefined || !baseline) return baseline;
  const added = addedDayIndicesForDefault(draftDefault);
  if (sameDaySet(added, baseline.addedDayIndices)) return baseline;
  return { ...baseline, addedDayIndices: added };
}

/** `{ start, end }` as date-only strings, or `null` when either is missing.
 *  END-INCLUSIVE since the 2026-08-24 owner ruling — `end` IS the last worked
 *  day, matching `generateWeeks` above and the server's `enumerateCrewWeeks`. */
function engagementRange(startDate, endDate) {
  const s = startDate ? String(startDate).slice(0, 10) : null;
  const e = endDate ? String(endDate).slice(0, 10) : null;
  return s && e ? { start: s, end: e } : null;
}

/** Is a date-only string inside `range`? `[start, end]` — the day ON
 *  `end_date` is worked (owner ruling, 2026-08-24; it was `[start, end)`
 *  before that). A range of `null` means "nothing was stated", and every
 *  caller below decides for itself what that means rather than having a
 *  default smuggled in here. */
function inRange(range, dateStr) {
  return !!range && !!dateStr && dateStr >= range.start && dateStr <= range.end;
}

export function buildWeekDayBaseline(member, weeks) {
  if (!member || !Array.isArray(weeks) || weeks.length === 0) return EMPTY_DAY_BASELINE;

  const rows = Array.isArray(member.days) ? member.days : [];
  const workedDates = new Set(rows.map((r) => r.work_date));
  // `is_presumed` is 1/0 on SQLite and true/false on Postgres — both shapes.
  const presumedDates = new Set(
    rows.filter((r) => r.is_presumed === 1 || r.is_presumed === true).map((r) => r.work_date)
  );
  const hasDayRows = workedDates.size > 0;
  // 🔀 2026-10-07: a human's "works no days" (`days_explicit_zero`, which
  // GET /crew-members publishes through its `cm.*`) draws NOTHING — not the
  // allocation fallback below — and a drag adds only its moved first/last
  // day, exactly as `server/helpers/crewDays.js#planPresumedDayRederivation`
  // lays it. Without this the picture showed allocation days the member was
  // never costed for.
  const explicitZero = !hasDayRows && Number(member.days_explicit_zero) === 1;

  const overrideMap = {};
  for (const o of member.overrides || []) overrideMap[o.week_start] = Number(o.allocation);
  const defaultAlloc = Number.isFinite(Number(member.default_allocation))
    ? Number(member.default_allocation)
    : 1.0;

  const base = {};
  const presumedWeeks = new Set();
  // 🔀 K01 (owner, 2026-10-06, option a): a STORED week whose allocation is 0
  // and which holds no worked day is OFF, and a drag that adds days into it
  // lays nothing there. Exactly `server/helpers/crewDays.js#
  // clipAndExtendPresumedDates`' seam block; pinned by `crewDaysShownEqualsCosted`.
  const offWeeks = new Set();
  for (const ws of weeks) {
    if (hasDayRows) {
      const indices = [];
      let allPresumed = true;
      for (let i = 0; i < 7; i++) {
        const date = dateForWeekDay(ws, i);
        if (date && workedDates.has(date)) {
          indices.push(i);
          if (!presumedDates.has(date)) allPresumed = false;
        }
      }
      base[ws] = indices;
      if (indices.length > 0 && allPresumed) presumedWeeks.add(ws);
    } else if (explicitZero) {
      base[ws] = [];
    } else {
      const alloc = ws in overrideMap ? overrideMap[ws] : defaultAlloc;
      base[ws] = daysForPreset(dayCountForAllocation(alloc));
      // Nothing at all is recorded for this member, so every week of it is an
      // assumption rather than a record.
      presumedWeeks.add(ws);
    }
    const weekAlloc = ws in overrideMap ? overrideMap[ws] : defaultAlloc;
    if (weekAlloc === 0 && base[ws].length === 0) offWeeks.add(ws);
  }
  /* 🔑 THE RANGE IS PART OF THE BASELINE, ADDED 2026-08-23, AND IT IS WHAT
     MAKES "a day nobody has said anything about" EXPRESSIBLE. `base[ws]` is a
     list of day INDICES, so a day that is missing from it is indistinguishable
     from a day that was deliberately left off — until you also know which
     dates this baseline had an opinion about. It had one about exactly the
     dates inside the member's STORED engagement; everything else is silence,
     and `resolveWeekDays` fills silence with the owner's ruled Mon–Fri default.
     Without it, a drag that pulls the start of a bar back into a week the
     baseline already knew handed those new days straight to the "not worked"
     branch — the reported defect. */
  return {
    base, presumedWeeks, hasDayRows, offWeeks, explicitZero,
    range: engagementRange(member.start_date, member.end_date),
    // 2026-10-19: the days a date change adds, at the STORED default (a page
    // with a draft default lays `withDraftDefaultAllocation` over this).
    addedDayIndices: addedDayIndicesForDefault(member.default_allocation),
  };
}

/**
 * 🔑 THE RULED DEFAULT FOR A DAY NOBODY HAS EXPRESSED AN OPINION ABOUT:
 * **Mon–Fri worked, Sat/Sun off.** Owner, 2026-08-23, after dragging the start
 * of a Gantt bar out: *"when I drag to extend the beginning of a gantt chart
 * bar it seems to default to off or at least take the first day as the default
 * for the rest of the new days. the default should always be monday to friday
 * on and saturday to sunday off."*
 *
 * 🔴 IT IS A RULING, NOT A DERIVATION, AND THAT IS THE WHOLE POINT. It
 * replaced `daysForPreset(dayCountForAllocation(alloc))`, which answered from
 * the member's allocation and so gave a 0.6 member Mon–Wed and an **"Off"**
 * member no days at all. The owner was offered the allocation-shaped answer and
 * ruled against it: a day the user has just brought into an engagement is a day
 * they intend to work, whatever fraction the REST of the engagement runs at.
 *
 * 🪤 It is also why the `parseFloat(default_allocation) || 1.0` falsy collapse
 * that used to sit behind this was DELETED rather than corrected. Reading an
 * explicit `0` as `0` — the "obvious" fix — would have made every new day of an
 * "Off" member arrive OFF, which is this defect made worse, not better. The
 * allocation is no longer consulted for this question at all, so there is
 * nothing left to collapse.
 *
 * 🔀 SUPERSEDED FOR A DRAG, 2026-10-19 (owner): *"a drag now defaults to 3
 * days"* — the days a date change adds are the CURRENT default's first N
 * weekdays (`addedDayIndicesForDefault`); this list is now the N = 5 case and
 * the fallback for a baseline that
 * carries no `addedDayIndices`. The 08-23 reasoning below is history.
 *
 * 🔑 INDEX 0 IS MONDAY, the convention `DAY_INITIALS` / `DAY_NAMES` /
 * `daysForPreset` / `dateForWeekDay` already share. It is deliberately a plain
 * literal rather than `daysForPreset(5)`: the ruling is "Mon–Fri", not "five
 * days", and the two stop being the same sentence the moment anybody changes
 * what a five-day preset means.
 */
export const DEFAULT_WORKED_DAY_INDICES = [0, 1, 2, 3, 4];

/**
 * The day indices of `weekStart` that the CURRENT engagement contains, that the
 * BASELINE's engagement did not, and that the ruled default says are worked.
 * The empty array when there are none — which is the ordinary case, so
 * `resolveWeekDays` can short-circuit on it and return the stored array
 * untouched (identity matters: `dayPayloadHasChanges` compares day sets, and a
 * fresh array per call would not change the answer but would allocate on every
 * render of every week).
 *
 * 🚨 CLIPPED TO THE CURRENT RANGE, AND THAT CLIP IS LOAD-BEARING IN THE OTHER
 * DIRECTION TOO. Without it, a member who starts on a Wednesday would have
 * Monday and Tuesday of their first week counted as "not covered by the
 * baseline" and silently ticked on — with no drag at all, on first render.
 * SHRINKING an engagement must likewise add nothing: those days are outside
 * the current range and so are never fresh.
 */
function newlyInRangeDefaultDays(baseline, weekStart, currentRange) {
  if (!currentRange) return [];
  const isOffSeam = (ws) => !!(baseline && baseline.offWeeks && baseline.offWeeks.has(ws));
  if (isOffSeam(weekStart)) return [];   // K01 (a): an Off week already in range stays Off
  if (baseline && baseline.explicitZero) return [];   // 10-07: only the moved first/last day
  const isNew = (date) => !!date && inRange(currentRange, date)
    && !inRange(baseline && baseline.range, date);
  // 🔀 2026-10-19 ("Default allocation"): the CURRENT default's first N
  // weekdays, not a flat Mon–Fri. A baseline without the field (an editor's
  // EMPTY_DAY_BASELINE) keeps the ruled Mon–Fri.
  const added = (baseline && Array.isArray(baseline.addedDayIndices))
    ? baseline.addedDayIndices : DEFAULT_WORKED_DAY_INDICES;
  const out = [];
  for (const i of added) {
    if (isNew(dateForWeekDay(weekStart, i))) out.push(i);
  }
  /* 🔀 OWNER RULING Q4, 2026-10-06 — THE SERVER'S WEEKEND-BOUNDARY RULE,
     MIRRORED. A typed start/end on a WEEKEND that is new to the range is
     WORKED, and the bridge fills the one gap that connects it to a run:
     Sunday END → its Saturday when that week's Friday is laid; Saturday
     START → its Sunday when the following Monday is laid. Exactly
     `server/helpers/crewDayBackfill.js#bridgeWeekendBoundary` over
     `crewDays.js#clipAndExtendPresumedDates`' set (owner ruling 2026-08-27),
     so the picture is the save. "Laid" = in the current range AND (stored, or a
     new weekday). A bridged day must itself be NEW: a weekend day inside the
     stored range was the stored set's call (Q3), on both sides.
     🪤 A PRESENTATION mirror, never a gate — parity is pinned by the shared
     scenario table run on both sides (`crewDaysShownEqualsCosted`). */
  const sat = dateForWeekDay(weekStart, 5);
  const sun = dateForWeekDay(weekStart, 6);
  const laid = (date) => {
    if (!inRange(currentRange, date)) return false;
    const ws = weekOfDate(date);
    const idx = ws ? dayIndexInWeek(ws, date) : -1;
    if (isOffSeam(ws)) return false;
    const stored = baseline && baseline.base ? baseline.base[ws] : undefined;
    if (inRange(baseline && baseline.range, date)) return Array.isArray(stored) && stored.includes(idx);
    return added.includes(idx);
  };
  const bounds = new Set([currentRange.start, currentRange.end]);
  for (const [i, date] of [[5, sat], [6, sun]]) {
    if (bounds.has(date) && isNew(date)) out.push(i);
  }
  if (sun === currentRange.end && isNew(sun) && isNew(sat) && laid(dateForWeekDay(weekStart, 4))) {
    out.push(5);
  }
  if (sat === currentRange.start && isNew(sat) && isNew(sun) && laid(dateForWeekDay(weekStart, 7))) {
    out.push(6);
  }
  return [...new Set(out)].sort((a, b) => a - b);
}

/** The Monday of a date-only string's week ('YYYY-MM-DD'), local-midnight
 *  parse — the module's own idiom; `null` for an unusable date. */
function weekOfDate(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const back = (d.getDay() + 6) % 7;   // index 0 is MONDAY
  return dateForWeekDay(dateStr, -back);
}

/** The Monday-first index of `dateStr` inside the week starting `weekStart`. */
function dayIndexInWeek(weekStart, dateStr) {
  for (let i = 0; i < 7; i++) if (dateForWeekDay(weekStart, i) === dateStr) return i;
  return -1;
}

/**
 * THE DAY SET A WEEK SHOWS RIGHT NOW. **The single answer to "what days does
 * this week have" — every render, every tick handler and every save payload on
 * every surface calls THIS, and none of them may keep a copy of the rule.**
 *
 * 🔴 IT USED TO HAVE TWO ANSWERS AND THE DISAGREEMENT COST REAL MONEY.
 * `weekDayBaseline` is seeded ONCE, at edit-open, from the member's STORED
 * dates (`handleExpandMember`), while the grid's weeks come from the LIVE
 * form. Extend `end_date` and the new weeks sit in NEITHER the overlay NOR
 * the baseline. The render fell back to the allocation and drew five ticked
 * days; this function returned `[]`. The save then posted a `range_start` →
 * `range_end` spanning every week with days for only the old ones, the
 * server's replace-range deleted the freshly re-derived rows for the rest,
 * and — because every row `PUT /days` writes carries `is_presumed = 0` —
 * the member became PERMANENTLY exempt from re-derivation. A 4.2-week
 * engagement was costed as 2.2 weeks of labour: ~48% understated, silent,
 * permanent, and always in the "budget looks healthier than it is"
 * direction, while rental and allowances stayed on the allocation and
 * remained right. `GET /report` then contradicted itself in one payload —
 * the `weekly_rate_override` shape.
 *
 * 🔑 BOTH `!== undefined` CHECKS ARE LOAD-BEARING, IN BOTH DIRECTIONS.
 *  • ABSENT (no key) means "nobody has said anything about this week" → fall
 *    back to its allocation, which is what the user is being charged for and
 *    what the editor draws.
 *  • An explicit `[]` means "this week is deliberately EMPTY" — the ruled
 *    behaviour is *"Unticking EVERY day means ZERO PAY — not a fallback to
 *    the allocation"*. A truthiness or `.length` test here reads a decision
 *    as an absence and silently re-charges the week. Pinned by
 *    `BudgetCostCodes.dayGrid.test.js` §6 THE INVERSE, and — since the Gantt
 *    became a second editor — by `CrewGanttView.dayToggle.test.js` §4, which
 *    proves an emptied week SURVIVES a collapse of the Gantt row.
 *
 * 🔻 THE FOURTH ARGUMENT IS THE **CURRENT** ENGAGEMENT RANGE, NOT AN
 * ALLOCATION — CHANGED 2026-08-23 BY OWNER RULING. It used to be the week's
 * allocation, and an unknown week resolved to
 * `daysForPreset(dayCountForAllocation(alloc))`. Owner, having dragged the
 * start of a Gantt bar: *"the default should always be monday to friday on and
 * saturday to sunday off."* Always — not a shape derived from the member's
 * allocation, which gave a 0.6 member Mon–Wed and an "Off" member nothing at
 * all. See `DEFAULT_WORKED_DAY_INDICES`.
 *
 * @param currentRange `{ start, end }` — the engagement as it stands RIGHT NOW,
 *   including any unsaved drag or typed date. It is what makes "newly in range"
 *   answerable: a day is new when the CURRENT range contains it and the
 *   BASELINE's range did not. `null`/unusable means "not stated", and then no
 *   day is treated as new — the conservative reading, because inventing worked
 *   days is the direction that costs money.
 */
export function resolveWeekDays(overlay, baseline, weekStart, currentRange) {
  // A week the user has TOUCHED is their record — a manually unticked first or
  // last day stays off (owner, 2026-10-07: "…but can be manualy turned off").
  if (overlay && overlay[weekStart] !== undefined) return overlay[weekStart];
  const days = resolveWeekDaysBeforeBoundaryRule(overlay, baseline, weekStart, currentRange);
  const moved = movedEnds(baseline, currentRange);
  if (!moved.start && !moved.end) return days;
  const forced = boundaryRuleDayIndices(overlay, baseline, weekStart, currentRange, days, moved);
  if (forced.every((i) => days.includes(i))) return days;   // identity kept
  return [...new Set([...days, ...forced])].sort((a, b) => a - b);
}

/**
 * 🔀 OWNER RULINGS, Pete, 2026-10-07: *"Easy rule first and last day are always
 * worked"*, narrowed the same day: *"I think fist and last days are always
 * worked when dragged but can be manualy turned off?"*
 *
 * 🔑 A DATE-EDIT RULE: the NEW date at each end the draft MOVED (against the
 * STORED range the baseline was seeded from) is drawn worked — any weekday,
 * inside the stored range too, inside an Off week too — plus the 08-27 bridge.
 * A boundary that did not move is not forced; a touched week is never forced
 * (the user's untick is a record). Server twin:
 * `server/helpers/crewDayBackfill.js#withMovedBoundaryDays`, laid by the dates
 * PUT. A write + draft rule, never a read rule: with no moved end the picture
 * is the STORED set, exactly what GET costs.
 */
function movedEnds(baseline, currentRange) {
  if (!currentRange) return { start: false, end: false };
  const stored = baseline && baseline.range;
  if (!stored) return { start: true, end: true };
  return { start: stored.start !== currentRange.start, end: stored.end !== currentRange.end };
}

/** The day indices of `weekStart` the boundary rule lays: each MOVED end's
 *  date in this week, plus the 08-27 bridge connecting a forced weekend
 *  boundary to its run (Q-BRIDGE, reading A) — a Sunday END lays its Saturday
 *  when the Friday is laid, a Saturday START its Sunday when the next Monday
 *  is laid. Exactly the server's `withMovedBoundaryDays`. */
function boundaryRuleDayIndices(overlay, baseline, weekStart, range, days, moved) {
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = dateForWeekDay(weekStart, i);
    if (d && ((moved.start && d === range.start) || (moved.end && d === range.end))) out.push(i);
  }
  const laidHere = (i) => inRange(range, dateForWeekDay(weekStart, i))
    && (days.includes(i) || out.includes(i));
  if (out.includes(6) && dateForWeekDay(weekStart, 6) === range.end
      && inRange(range, dateForWeekDay(weekStart, 5)) && laidHere(4)) {
    out.push(5);
  }
  const nextMonday = dateForWeekDay(weekStart, 7);
  if (out.includes(5) && dateForWeekDay(weekStart, 5) === range.start
      && inRange(range, dateForWeekDay(weekStart, 6)) && inRange(range, nextMonday)
      && ((moved.end && nextMonday === range.end)
        || resolveWeekDaysBeforeBoundaryRule(overlay, baseline, nextMonday, range).includes(0))) {
    out.push(6);
  }
  return out;
}

/** `resolveWeekDays` WITHOUT the 2026-10-07 boundary rule — the overlay, the
 *  stored set and the newly-in-range default only. Module-private: the one
 *  other reader is `dateChangeWeekAllocations`, whose week counts must not
 *  include a forced day (the server lays it ADDITIVELY, on top of the count). */
function resolveWeekDaysBeforeBoundaryRule(overlay, baseline, weekStart, currentRange) {
  if (overlay && overlay[weekStart] !== undefined) return overlay[weekStart];
  const stored = clipStoredToMovedRange(
    baseline && baseline.base ? baseline.base[weekStart] : undefined, baseline, weekStart, currentRange
  );
  const fresh = newlyInRangeDefaultDays(baseline, weekStart, currentRange);
  if (stored === undefined) {
    // A week neither editor has ever had an opinion about. With a usable range
    // that is exactly `fresh`; without one there is nothing to clip against, so
    // the ruled default stands whole.
    return currentRange ? fresh : DEFAULT_WORKED_DAY_INDICES.slice();
  }
  if (fresh.length === 0) return stored;
  return [...new Set([...stored, ...fresh])].sort((a, b) => a - b);
}

/**
 * 🔀 THE DROP HALF OF Q1, MIRRORED (2026-10-07 — the owner's Saturday bug).
 * Once the draft has MOVED an end, a stored day outside the CURRENT range is
 * not part of the draft: the server's `clipAndExtendPresumedDates` keeps only
 * `[newStart, newEnd]` and the hand-set planner DELETES every stored row outside
 * it (owner ruling Q1, 2026-10-06 — "a SAVED shrink drops them"). The client
 * mirrored only the ADD half (`newlyInRangeDefaultDays`), and the Gantt's bar
 * clamp hid the gap: the resolved set still held rows the bar never drew, and
 * the moment any toggle fired, `buildDayPayload` posted them — so one save's
 * `/days` re-inserted the rows its own dates PUT had just deleted. Measured on
 * staging (member 303, three stored rows outside her range, end dragged onto a
 * Saturday): ticked 71 days, unticked 73 — ticking a worked day LOWERED the
 * price. Pinned by `crewDayResolution.outOfRangeRows.test.js` and the harness's
 * "no date outside the draft range" assertion.
 *
 * 🔻 NOT a read rule: with NO moved end the stored set stands whole, even a
 * legacy row outside [start, end] that sits inside the costed Monday window —
 * GET costs it and this is where the picture meets the money. Whether such a
 * row is drawn, costed or kept is NOT ruled (§3 of that test pins the status
 * quo); do not widen this to "always" without the owner.
 *
 * Identity kept when nothing is clipped (`dayPayloadHasChanges` compares sets;
 * the ordinary case allocates nothing).
 */
function clipStoredToMovedRange(stored, baseline, weekStart, currentRange) {
  if (!Array.isArray(stored) || stored.length === 0 || !currentRange) return stored;
  const moved = movedEnds(baseline, currentRange);
  if (!moved.start && !moved.end) return stored;
  const kept = stored.filter((i) => inRange(currentRange, dateForWeekDay(weekStart, i)));
  return kept.length === stored.length ? stored : kept;
}

export const sameDaySet = (a, b) => {
  const x = [...(a || [])].sort((p, q) => p - q);
  const y = [...(b || [])].sort((p, q) => p - q);
  return x.length === y.length && x.every((v, i) => v === y[i]);
};

/**
 * Has the user actually CHANGED any week's day set?
 *
 * 🔑 "Changed" is measured against WHAT THE WEEK SHOWED BEFORE THE EDIT — it
 * calls `resolveWeekDays` with no overlay rather than reading `baseline.base`
 * directly, so it asks the SAME question the render asked. For a newly-in-range
 * week that answer is `newlyInRangeDefaultDays`, i.e. the ruled Mon–Fri default
 * CLIPPED TO THE CURRENT RANGE — not `[]`. Comparing against the raw baseline
 * would call a no-op edit on such a week a change — and a change posts a full
 * day set, which FREEZES that member's schedule (`is_presumed = 0`) on a save
 * that changed nothing.
 *
 * 🔻 THIS LINE SAID "its **allocation fallback**" UNTIL 2026-08-23 AND THAT WAS
 * THE MECHANISM ITS OWN COMMIT DELETED. `4bffd3d9` changed this function's
 * fourth argument from `fallbackAllocFor` to `currentRange`; the sentence
 * survived as unchanged diff context directly above the changed signature,
 * which is exactly where a stale comment hides. The module header and
 * `DEFAULT_WORKED_DAY_INDICES` both already say the allocation is no longer
 * consulted for this question at all — this line was the third voice and the
 * only one still describing the old world. "Newly-in-range week" is precisely
 * the concept whose answer changed. Pinned by
 * `BudgetCostCodes.newDayDefaults.test.js` §2.1 *"a 3-day-week member still gets
 * Mon–Fri on the days a drag adds"* and §3, *"a member whose default allocation
 * is an explicit 0"*, §3.1 *"new days are Mon–Fri, and the stored Off weeks stay
 * empty"*. Both green; both are the allocation not being consulted.
 *
 * This is the shared half of two things that must never drift apart: the DIRTY
 * flag (may I collapse this row?) and the SAVE decision (do I send a request at
 * all?). They were two copies of one rule inside `BudgetCostCodes.js`; they are
 * one function now, and both surfaces call it.
 */
export function dayPayloadHasChanges(weeks, overlay, baseline, currentRange) {
  return weeks.some(
    (ws) => overlay[ws] !== undefined
      && !sameDaySet(
        overlay[ws],
        resolveWeekDays(undefined, baseline, ws, currentRange)
      )
  );
}

/**
 * The `PUT /crew-members/:id/days` body, or NULL when there is nothing to send.
 *
 * Returns null when the engagement has no weeks (nothing to replace) or when no
 * week's day set differs from its baseline — see `dayPayloadHasChanges` for why
 * "no change" must mean "no request" rather than "a harmless request".
 *
 * ⚠️ The RANGE is the visible week span, not the posted days' min/max — an empty
 * `days` array has no min and no max, so a derived range could never clear a
 * week. It is the caller's statement of what is being REPLACED, independent of
 * what it is replaced with. The server validates it against
 * `crewWeekSpan(enumerateCrewWeeks(...))`, and since 2026-08-24 the two loops
 * carry the IDENTICAL end-inclusive bound (`current <= end`), so a range built
 * this way is exactly the permitted window — never outside it.
 */
export function buildDayPayload({ startDate, endDate, overlay, baseline }) {
  const weeks = generateWeeks(startDate, endDate);
  if (weeks.length === 0) return null;
  const currentRange = engagementRange(startDate, endDate);
  if (!dayPayloadHasChanges(weeks, overlay, baseline, currentRange)) return null;

  const days = [];
  for (const ws of weeks) {
    for (const idx of resolveWeekDays(overlay, baseline, ws, currentRange)) {
      const date = dateForWeekDay(ws, idx);
      if (date) days.push({ work_date: date });
    }
  }
  return {
    range_start: weeks[0],
    range_end: dateForWeekDay(weeks[weeks.length - 1], 6),
    days,
  };
}

/**
 * Which week, and which day OF that week, does a calendar date belong to?
 * `{ weekStart, dayIndex }`, or `null` when the date is outside `weeks`.
 *
 * 🔑 IT SEARCHES `dateForWeekDay` RATHER THAN DOING ARITHMETIC, and that is the
 * point: a surface that edits by DATE (the Crew Gantt's toggles) has to reach
 * the same `{ week, index }` pair the surface that edits by WEEK (the day grid)
 * uses, and a second Monday-snapping loop is exactly the duplication this module
 * exists to prevent — "index 0 is MONDAY" is a convention four functions already
 * share, and re-deriving it silently offsets every `work_date` written.
 * O(weeks × 7) over an engagement that is at most ~104 weeks; the day grid's own
 * `buildWeekDayBaseline` already walks the same shape.
 */
export function weekSlotForDate(weeks, dateStr) {
  for (const ws of weeks) {
    for (let i = 0; i < 7; i++) {
      if (dateForWeekDay(ws, i) === dateStr) return { weekStart: ws, dayIndex: i };
    }
  }
  return null;
}

/**
 * Every worked CALENDAR DATE across an engagement, as a `Set<'YYYY-MM-DD'>`,
 * resolved through `resolveWeekDays` exactly as the payload is.
 *
 * 🔑 THIS IS WHAT KEEPS A SECOND EDITOR HONEST, AND IT IS THE REASON THIS
 * FUNCTION EXISTS AT ALL. The Crew Gantt draws worked days as PIXELS and posts
 * them as DATES. If the pixels came from one resolution and the payload from
 * another, the user would be looking at a picture of a schedule they were not
 * saving — which is the 2026-08 defect rebuilt in a new place, with a prettier
 * surface to hide behind. Both now come from here, over the same
 * overlay + baseline, so they cannot disagree: **the fade IS the payload,
 * rendered.** Pinned by §3 of `CrewGanttView.dayToggle.test.js`.
 */
export function resolveWorkedDateSet({ startDate, endDate, overlay, baseline }) {
  return workedDateSetVia(resolveWeekDays, { startDate, endDate, overlay, baseline });
}

function workedDateSetVia(resolve, { startDate, endDate, overlay, baseline }) {
  const set = new Set();
  const currentRange = engagementRange(startDate, endDate);
  for (const ws of generateWeeks(startDate, endDate)) {
    for (const idx of resolve(overlay, baseline, ws, currentRange)) {
      const date = dateForWeekDay(ws, idx);
      if (date) set.add(date);
    }
  }
  return set;
}

/**
 * THE WEEK ALLOCATIONS A DATE CHANGE IMPLIES — `{ [week_start]: allocation }`
 * for every week whose in-range days the move CHANGED. Pure; no request.
 *
 * 🔀 2026-10-06, replacing the page's private `computeDateChangeOverrides`
 * arithmetic (moved here so it is testable and the harness can assert it).
 * Owner: *"Mon–Fri for everyone, like Q2"* — so a week a drag brings into range
 * is WORKED even when the member's default (or a stale override) says Off, and
 * the `/weeks` write that follows the dates must SAY so, or its from-scratch
 * re-lay takes the days back off. The old arithmetic counted weekdays and wrote
 * `null` ("full week") for a full one — which for a `default_allocation` 0
 * member means OFF, and on a Monday-start week DELETED the very override that
 * made week 1 worked: measured, £0 saved against 10 days drawn.
 *
 * 🔑 THE ALLOCATION IS READ OFF THE PICTURE, NOT RE-DERIVED: the drawn day set
 * (`resolveWorkedDateSet` over the new range, the STORED baseline, no overlay
 * — exactly what the Gantt shows for a date drag) counted per touched week —
 * its drawn WEEKDAYS (a weekend boundary counts only in a week with no in-range
 * weekday — the re-lay lays a boundary ON TOP of its count, 10-19 F1; a bridged
 * day is never counted) — through `allocationForDayCount`
 * (the clamp at 5). So a week already in range that is Off counts 0 and STAYS
 * Off — K01, owner option (a): nothing a drag adds into it is drawn or laid.
 *
 * 🪤 "Touched" = a week holding a WEEKDAY or a typed weekend boundary whose
 * in-range status changed (newly in, or newly out). A weekend day that merely
 * entered the range touches nothing — otherwise extending a hand-set member's
 * end past a weekend would rewrite the allocation of a week the user never
 * changed.
 */
export function dateChangeWeekAllocations(member, startDate, endDate, draftDefault) {
  const oldRange = engagementRange(member && member.start_date, member && member.end_date);
  const newRange = engagementRange(startDate, endDate);
  if (!newRange) return {};
  const newWeeks = generateWeeks(newRange.start, newRange.end);
  // 🔀 2026-10-19: `draftDefault` = the default the SAME save writes (level-2
  // panel / crew table form); the added days are counted at it.
  const baseline = withDraftDefaultAllocation(buildWeekDayBaseline(
    member, oldRange ? generateWeeks(oldRange.start, oldRange.end) : []
  ), draftDefault);
  // 🔀 2026-10-07 (moved first/last day): a WEEKDAY the rule forces is NOT
  // counted — the server lays it ON TOP of the count (`withMovedBoundaryDays`),
  // and counting it would make a `/weeks` re-lay add one more Monday-first
  // weekday instead. A forced WEEKEND boundary is NOT counted either, except
  // in a week holding no in-range weekday (10-19 F1 — see the count below).
  const args = { startDate: newRange.start, endDate: newRange.end, overlay: {}, baseline };
  const drawnBeforeRule = workedDateSetVia(resolveWeekDaysBeforeBoundaryRule, args);
  const drawnWithRule = workedDateSetVia(resolveWeekDays, args);
  const drawn = { has: (d, idx) => (idx <= 4 ? drawnBeforeRule : drawnWithRule).has(d) };
  const bounds = new Set([newRange.start, newRange.end]);
  const counts = (d, idx) => idx <= 4 || bounds.has(d);
  const out = {};
  for (const ws of newWeeks) {
    let touched = false;
    let weekdays = 0;
    let weekend = 0;
    let inRangeWeekday = false;
    for (let i = 0; i < 7; i++) {
      const d = dateForWeekDay(ws, i);
      if (!d || !counts(d, i)) continue;
      if (inRange(newRange, d) !== inRange(oldRange, d)) touched = true;
      if (i <= 4 && inRange(newRange, d)) inRangeWeekday = true;
      if (inRange(newRange, d) && drawn.has(d, i)) {
        if (i <= 4) weekdays += 1; else weekend += 1;
      }
    }
    // 🪤 A WEEKEND BOUNDARY IS NEVER PART OF A WEEKDAY COUNT. The `/weeks`
    // from-scratch re-lay (`engagementAnchoredWeekDays`) lays `count` weekdays
    // Monday-first and THEN the weekend boundary ON TOP, so counting the
    // boundary in lays one weekday nobody drew. 🔀 Fable review of e8276978
    // (F1, measured: 0.4, end → Sun 18 drew 5 / £1,000 and saved 6 / £1,200,
    // Wed 14 added) — this read `weekdays > 0 || !inRangeWeekday ? weekend`;
    // at a full week the clamp at 5 hid it. It counts ONLY in a week with no
    // in-range weekday at all, where a count of 0 would lay the boundary
    // nothing (a 0 count lays NOTHING); an Off seam holding a forced weekend
    // end has in-range weekdays, stays 0, and the dates PUT lays the day.
    let n = weekdays + (!inRangeWeekday ? weekend : 0);
    // 🔀 2026-10-19 (R4 at N < 5): a start moved into a brand-NEW week at or
    // after its default's last weekday (Thu at 0.6) draws ONLY the forced start
    // day, so the count above is 0 — and an override of 0 (≠ the default) makes
    // `/weeks` re-lay that week from scratch as Off, deleting the start day the
    // dates PUT just laid (preview ≠ save). Counted as 1 instead: the re-lay
    // anchors a week's weekdays at the engagement start, so it lays exactly
    // that day. Unreachable at N = 5 (a new week's start is always < 5).
    // (At a 0-day default a 0 count IS the default, so no override and no re-lay.)
    if (n === 0 && oldRange && newRange.start !== oldRange.start
        && Array.isArray(baseline.addedDayIndices) && baseline.addedDayIndices.length > 0) {
      const startIdx = dayIndexInWeek(ws, newRange.start);
      const touchesOld = [0, 1, 2, 3, 4, 5, 6].some((i) => inRange(oldRange, dateForWeekDay(ws, i)));
      if (startIdx >= 0 && startIdx <= 4 && !touchesOld && drawnWithRule.has(newRange.start)) n = 1;
    }
    // 🔀 2026-10-19: a K01 Off SEAM week gets NO date-change allocation. Its
    // Off-ness is a STORED fact (the stored override, or the stored default),
    // and K01 keeps it Off on both sides; writing 0 for it only matters when
    // the same save changes the DEFAULT (0 → N), and then the 0 reads as a
    // CHANGE to `/weeks`, whose from-scratch re-lay deletes the forced
    // first/last day the dates PUT just laid (DA08: drawn Fri 16, saved none).
    if (touched && !(baseline.offWeeks && baseline.offWeeks.has(ws))) out[ws] = allocationForDayCount(n);
  }
  return out;
}

/**
 * The FULL `overrides` array `PUT /crew-members/:id/weeks` gets after a date
 * change: the member's stored overrides pruned to the new weeks, with every
 * touched week set from `dateChangeWeekAllocations`. 🔑 **An override is
 * REMOVED only when its allocation equals the member's default** — "no
 * override" means "the default", which is a full week only for a default of
 * 1. Sorted by `week_start` so the array is deterministic.
 */
export function dateChangeWeekOverrides(member, startDate, endDate, draftDefault) {
  const newRange = engagementRange(startDate, endDate);
  if (!newRange) return [];
  const newWeeks = new Set(generateWeeks(newRange.start, newRange.end));
  // 🔀 2026-10-19: "equals the member's default" is the default the SAME save
  // writes — the server compares against the post-write row.
  const raw = draftDefault !== undefined ? draftDefault : (member ? member.default_allocation : undefined);
  const defaultAlloc = raw === null || raw === undefined || raw === ''
    || !Number.isFinite(Number(raw)) ? 1 : Number(raw);
  const map = {};
  for (const o of (member && member.overrides) || []) {
    if (newWeeks.has(o.week_start)) map[o.week_start] = Number(o.allocation);
  }
  for (const [ws, alloc] of Object.entries(dateChangeWeekAllocations(member, startDate, endDate, draftDefault))) {
    if (alloc === defaultAlloc) delete map[ws];
    else map[ws] = alloc;
  }
  return Object.keys(map).sort().map((week_start) => ({ week_start, allocation: map[week_start] }));
}

/**
 * THE `PUT /weeks` REPLACE-ALL ARRAY FOR ONE EDIT — the dates AND the day ticks
 * — on EVERY surface that edits a member's schedule (the Gantt strip's save and
 * cost preview, the crew table's save and cost preview).
 *
 * 🔀 OWNER RULING A, Pete, 2026-10-19: *the crew TABLE editor's date change
 * behaves exactly as a Gantt drag*. The table used to post its weeks AS LOADED,
 * so the same end move stored `[]` there and `[["2026-08-17",0.6]]` from the
 * Gantt (probe R9). One rule here, so the two cannot drift again.
 *
 * Base: when the dates MOVED from the stored member's, `dateChangeWeekOverrides`
 * (laid weeks keep theirs, new weeks at `draftDefault`); otherwise the stored
 * overrides. Then `touched` (`{ week_start: allocation }`, what the user's own
 * ticks imply) WINS on any week it names, filtered to the edit's weeks — an
 * explicit statement beats an inference, and a tick in a week the edit removed
 * must not recreate an override outside the dates.
 * 🪤 A missing date is NOT a move: with no range there is nothing to lay, and
 * `dateChangeWeekOverrides` would answer `[]` and wipe the stored set.
 */
export function weekOverridesForEdit({ member, startDate, endDate, draftDefault, touched }) {
  const next = engagementRange(startDate, endDate);
  const base = engagementDatesMoved(member, startDate, endDate)
    ? dateChangeWeekOverrides(member, next.start, next.end, draftDefault)
    : ((member && member.overrides) || []);
  const weeks = next ? generateWeeks(next.start, next.end) : [];
  const merged = {};
  for (const o of base) merged[o.week_start] = Number(o.allocation);
  for (const [ws, alloc] of Object.entries(touched || {})) {
    if (weeks.includes(ws)) merged[ws] = Number(alloc);
  }
  return Object.entries(merged).map(([week_start, allocation]) => ({ week_start, allocation }));
}

/** Did an edit MOVE the stored engagement? A missing draft date is not a move. */
function engagementDatesMoved(member, startDate, endDate) {
  const stored = engagementRange(member && member.start_date, member && member.end_date);
  const next = engagementRange(startDate, endDate);
  return !!next && (!stored || stored.start !== next.start || stored.end !== next.end);
}

/**
 * THE `PUT /weeks` BODY BOTH SAVES SEND (the Gantt strip, the crew table).
 * 🔀 OWNER RULING B, Pete, 2026-10-20 ("yes go with B" — *"Will it always save
 * what I drag?"*): when the edit MOVED the dates, the body says so with
 * `date_change_save: true` and the server re-lays NO day rows — the dates PUT
 * just laid the drawn days, and a Monday-first re-lay of a changed week holding a
 * gap or a forced first/last day stored other dates (PA03, PA05, T09). Never sent
 * otherwise: an allocation-only write must still re-lay its changed weeks (C1).
 */
export function weeksSaveBody({ member, startDate, endDate, overrides }) {
  return engagementDatesMoved(member, startDate, endDate)
    ? { overrides, date_change_save: true }
    : { overrides };
}

/**
 * A cost preview's `week_overrides`: `overrides` (from `weekOverridesForEdit`),
 * or `null` — "use the stored rows" — when it is the member's stored set.
 * 🪤 Compares the RESULT, never "has a key been touched": an undone tick leaves
 * a key whose value is back to the stored one (`dayTogglePreview.test.js` §6.1).
 */
export function draftWeekOverridesOrNull(member, overrides) {
  const canon = (arr) => (arr || []).map((o) => `${o.week_start}=${Number(o.allocation)}`).sort().join('|');
  return canon(overrides) === canon(member && member.overrides) ? null : overrides;
}
